import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { toast } from "sonner";
import { Mail, ArrowRight, ArrowLeft, Loader2 } from "lucide-react";
import { Brand } from "@/components/Brand";

const HERO = "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwyfHxmb3VudGFpbiUyMHBlbiUyMHNpZ25pbmclMjBkb2N1bWVudHxlbnwwfHx8fDE3OTEwMjk1MDl8MA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("consumer");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const requestOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/request-otp", { email, name, role });
      setStep("otp");
      if (data.dev_otp) toast.info(`Dev mode code: ${data.dev_otp}`, { duration: 10000 });
      else toast.success("Sign-in code sent to your email.");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Could not send code");
    } finally {
      setLoading(false);
    }
  };

  const verify = async (value) => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-otp", { email, code: value });
      login(data.user, data.token);
      toast.success(`Welcome, ${data.user.name}`);
      navigate("/");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Invalid code");
      setCode("");
    } finally {
      setLoading(false);
    }
  };

  const roles = [
    { v: "consumer", t: "Policyholder", d: "Guided help to challenge your claim" },
    { v: "consultant", t: "Team / Consultant", d: "Full case review workspace" },
  ];

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      {/* Left hero */}
      <div className="relative hidden flex-col justify-between bg-primary p-12 text-primary-foreground lg:flex">
        <img src={HERO} alt="Signing a legal document" className="absolute inset-0 h-full w-full object-cover opacity-20" />
        <div className="relative">
          <Brand light />
        </div>
        <div className="relative max-w-md">
          <h1 className="font-heading text-4xl font-bold leading-tight">
            Evidence-first letters to challenge unfair claim decisions.
          </h1>
          <p className="mt-4 text-base text-white/70">
            Upload your policy, rejection letter and claim file. We draft a fact-bound, law-firm-style
            representation under Indian consumer-law principles — always reviewed before issue.
          </p>
        </div>
        <p className="relative text-xs text-white/50">
          Drafts are representations subject to human review. Not a substitute for legal advice.
        </p>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center bg-background p-6">
        <div className="w-full max-w-md">
          {step === "email" ? (
            <form onSubmit={requestOtp} className="space-y-6" data-testid="login-email-form">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Sign in</p>
                <h2 className="mt-1 font-heading text-3xl font-bold text-primary">Access your cases</h2>
                <p className="mt-2 text-sm text-muted-foreground">We'll email you a one-time code — no password needed.</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input id="email" data-testid="login-email-input" type="email" required placeholder="you@email.com"
                    value={email} onChange={(e) => setEmail(e.target.value)} className="pl-9" />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="name">Name <span className="text-muted-foreground">(new accounts)</span></Label>
                <Input id="name" data-testid="login-name-input" placeholder="Your name"
                  value={name} onChange={(e) => setName(e.target.value)} />
              </div>

              <div className="space-y-2">
                <Label>I am a…</Label>
                <div className="grid grid-cols-2 gap-3">
                  {roles.map((r) => (
                    <button type="button" key={r.v} data-testid={`role-${r.v}-btn`} onClick={() => setRole(r.v)}
                      className={`rounded-md border p-3 text-left transition-colors duration-200 ${role === r.v ? "border-accent bg-accent/5 ring-1 ring-accent" : "border-border hover:border-primary/40"}`}>
                      <div className="text-sm font-semibold text-primary">{r.t}</div>
                      <div className="mt-0.5 text-xs text-muted-foreground">{r.d}</div>
                    </button>
                  ))}
                </div>
              </div>

              <Button type="submit" data-testid="send-code-btn" disabled={loading} className="w-full gap-2 bg-accent text-accent-foreground hover:bg-accent/90">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                Send sign-in code
              </Button>
            </form>
          ) : (
            <div className="space-y-6" data-testid="login-otp-form">
              <button onClick={() => setStep("email")} data-testid="otp-back-btn" className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary">
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
              <div>
                <h2 className="font-heading text-3xl font-bold text-primary">Enter your code</h2>
                <p className="mt-2 text-sm text-muted-foreground">Sent to <span className="font-medium text-primary">{email}</span></p>
              </div>
              <div className="flex justify-center py-2">
                <InputOTP maxLength={6} value={code} onChange={(v) => { setCode(v); if (v.length === 6) verify(v); }} data-testid="otp-input">
                  <InputOTPGroup>
                    {[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} className="h-12 w-12 text-lg" />)}
                  </InputOTPGroup>
                </InputOTP>
              </div>
              <Button onClick={() => verify(code)} disabled={loading || code.length < 6} data-testid="verify-code-btn" className="w-full gap-2 bg-accent text-accent-foreground hover:bg-accent/90">
                {loading && <Loader2 className="h-4 w-4 animate-spin" />} Verify & sign in
              </Button>
              <button onClick={requestOtp} className="w-full text-center text-sm text-muted-foreground hover:text-primary" data-testid="resend-code-btn">
                Didn't get it? Resend code
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
